// Copyright (c) Microsoft. All rights reserved.

import { AuthenticatedTemplate, UnauthenticatedTemplate, useIsAuthenticated, useMsal } from '@azure/msal-react';
import { FluentProvider, Subtitle1, makeStyles, shorthands, tokens } from '@fluentui/react-components';

import * as React from 'react';
import { useEffect } from 'react';
import Chat from './components/chat/Chat';
import { Loading, Login } from './components/views';
import { AuthHelper } from './libs/auth/AuthHelper';
import { useChat, useFile, usePlugins } from './libs/hooks';
// import { AlertType } from './libs/models/AlertType';
import { useAppDispatch, useAppSelector } from './redux/app/hooks';
import { RootState } from './redux/app/store';
import { FeatureKeys } from './redux/features/app/AppState';
import { setActiveUserInfo, setServiceInfo } from './redux/features/app/appSlice';
import { Breakpoints, semanticKernelDarkTheme, semanticKernelLightTheme } from './styles';
// import { updateBotResponseStatus } from './redux/features/conversations/conversationsSlice';
// import { ChatMessageType } from './libs/models/ChatMessage';
// import MessageListener from './components/chat/MessageListener';

export const useClasses = makeStyles({
    container: {
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        width: '100%',
        ...shorthands.overflow('hidden'),
    },
    logo: {
        color: '#F25C24',
        margin: '0px',
        padding: '0px !important'
    },
    logoPosition: {
        display: 'flex',
        justifyContent: 'flex-start',
    },
    drawerIcon: {
        margin: '0px 12px',
        color: 'white',
        display: 'none',
        ...Breakpoints.small({
            display: 'block'
        })
    }
    ,
    header: {
        fontStyle: 'italic',
        fontFamily: 'Source Sans Pro',
        alignItems: 'center',
        backgroundColor: '#002E61',
        color: tokens.colorNeutralForegroundOnBrand,
        display: 'flex',
        '& h1': {
            paddingLeft: tokens.spacingHorizontalXL,
            display: 'flex',
        },
        height: '48px',
        justifyContent: 'space-between',
        width: '100%',
        ...Breakpoints.small({
            justifyContent: 'space-between',
        })
    },
    persona: {
        marginRight: tokens.spacingHorizontalXXL,
    },
    cornerItems: {
        display: 'flex',
        ...shorthands.gap(tokens.spacingHorizontalS),
    },
});

export enum AppState {
    ProbeForBackend,
    SettingUserInfo,
    ErrorLoadingChats,
    ErrorLoadingUserInfo,
    LoadingChats,
    Chat,
    SigningOut,
}

const App = () => {
    const classes = useClasses();
    const plugin = usePlugins();

    const [appState, setAppState] = React.useState(AppState.ProbeForBackend);
    const dispatch = useAppDispatch();
    // const { selectedId } = useAppSelector((state: RootState) => state.conversations);
    const { instance, inProgress } = useMsal();
    const { features, isMaintenance } = useAppSelector((state: RootState) => state.app);
    const isAuthenticated = useIsAuthenticated();

    const chat = useChat();
    const file = useFile();

    useEffect(() => {
        if (isMaintenance && appState !== AppState.ProbeForBackend) {
            setAppState(AppState.ProbeForBackend);
            return;
        }

        if (isAuthenticated && appState === AppState.SettingUserInfo) {
            const account = instance.getActiveAccount();
            if (!account) {
                setAppState(AppState.ErrorLoadingUserInfo);
            } else {
                dispatch(
                    setActiveUserInfo({
                        id: `${account.localAccountId}.${account.tenantId}`,
                        email: account.username, // username is the email address
                        username: account.name ?? account.username,
                    }),
                );

                //TODO:ITM-DEV for onsite.com users or 
                // Privacy disclaimer for internal Microsoft users
                // // if (account.username.split('@')[1] === 'microsoft.com') {
                // //     dispatch(
                // //         addAlert({
                // //             message:
                // //                 'By using Ontelligent Chat, you agree to protect sensitive data, not store it in chat, and allow chat history collection for service improvements. This tool is for internal use only.',
                // //             type: AlertType.Info,
                // //         }),
                // //     );
                // // }

                setAppState(AppState.LoadingChats);
            }
        }

        if ((isAuthenticated || !AuthHelper.isAuthAAD()) && appState === AppState.LoadingChats) {
            void Promise.all([
                // Load all chats from memory
                chat.loadChats()
                    .then(() => {
                        setAppState(AppState.Chat);
                    })
                    .catch(() => {
                        setAppState(AppState.ErrorLoadingChats);
                    }),

                plugin.loadCustomPlugin(),

                // Check if content safety is enabled
                file.getContentSafetyStatus(),

                // Load service information
                chat.getServiceInfo().then((serviceInfo) => {
                    if (serviceInfo) {
                        dispatch(setServiceInfo(serviceInfo));
                    }
                }),
            ]);




            // // eslint-disable-next-line @typescript-eslint/require-await
            // const handleMessage = async (event: MessageEvent) => {
            //     if (event.data && typeof event.data === 'object') {
            //         const { key, dashboardQuery } = event.data as { key?: string; dashboardQuery?: string };

            //         if (key === 'ontelligentQuery' && dashboardQuery) {
            //             console.log('Dashboard Query:', dashboardQuery);
            //             void chat.createChat();

            //             dispatch(editConversationInput({ id: selectedId, newInput: dashboardQuery }));

            //             // Perform any async operations here
            //             // Example: await someAsyncFunction(dashboardQuery, selectedId);
            //         }
            //     }
            // };

            // // eslint-disable-next-line @typescript-eslint/no-misused-promises
            // window.addEventListener('message', handleMessage);
            // // return () => {
            // //     // eslint-disable-next-line @typescript-eslint/no-misused-promises
            // //     window.removeEventListener('message', handleMessage);
            // // };


            // void chat.createChat();

            //dispatch(editConversationInput({ id: selectedId, newInput: 'test' }));
            //setValue('');
            //dispatch(editConversationInput({ id: selectedId, newInput: '' }));
            // dispatch(updateBotResponseStatus({ chatId: selectedId, status: 'Calling the kernel' }));
            //void chat.getResponse({ value: 'test', messageType: ChatMessageType.Message, chatId: selectedId });

        }



        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [instance, inProgress, isAuthenticated, appState, isMaintenance]);


    // const dispatch = useAppDispatch();



    useEffect(() => {
        ////console.log('Ontelligent 1 App.tsx: useEffect: MessageListener');
        // eslint-disable-next-line @typescript-eslint/require-await
        const handleMessage = async (event: MessageEvent) => {
            if (event.data && typeof event.data === 'object') {
                const { key, dashboardQuery } = event.data as { key?: string; dashboardQuery?: string };

                if (key === 'ontelligentQuery' && dashboardQuery) {
                    // // debugger;
                    ////console.log('Ontelligent 2 Dashboard Query:', dashboardQuery);
                    localStorage.setItem('ontelligentQuery', dashboardQuery);
                    await chat.createChat();

                    //dispatch(editConversationInput({ id: selectedId, newInput: dashboardQuery }));

                    // Perform any async operations here
                    // Example: await someAsyncFunction(dashboardQuery, selectedId);
                }
            }
        };

        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        window.addEventListener('message', handleMessage);
        return () => {
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            window.removeEventListener('message', handleMessage);
        };
    }, []);

    const content = <Chat classes={classes} appState={appState} setAppState={setAppState} />;
    return (
        <FluentProvider
            className="app-container"
            theme={features[FeatureKeys.DarkMode].enabled ? semanticKernelDarkTheme : semanticKernelLightTheme}
        >
            {AuthHelper.isAuthAAD() ? (
                <>
                    <UnauthenticatedTemplate>
                        <div className={classes.container}>
                            <div className={classes.header}>
                                <Subtitle1 as="h1">Ontelligent Chat</Subtitle1>
                            </div>
                            {appState === AppState.SigningOut && <Loading text="Signing you out..." />}
                            {appState !== AppState.SigningOut && <Login />}
                        </div>
                    </UnauthenticatedTemplate>
                    <AuthenticatedTemplate>
                        <>
                            {/* <MessageListener /> */}
                            {content}
                        </>
                    </AuthenticatedTemplate>
                </>
            ) : (
                <>
                    {/* <MessageListener /> */}
                    {content}
                </>
            )}
        </FluentProvider>
    );
};

export default App;
